/**
 * 主题配置工具函数
 */

// 设置 CSS 变量
export function setCssVar(key, value) {
  document.documentElement.style.setProperty(key, value)
}

// 颜色混合
export function mix(color1, color2, ratio) {
  const hex = (x) => {
    const v = x.toString(16)
    return v.length === 1 ? '0' + v : v
  }
  const r = Math.ceil(parseInt(color1.substring(1, 3), 16) * ratio + parseInt(color2.substring(1, 3), 16) * (1 - ratio))
  const g = Math.ceil(parseInt(color1.substring(3, 5), 16) * ratio + parseInt(color2.substring(3, 5), 16) * (1 - ratio))
  const b = Math.ceil(parseInt(color1.substring(5, 7), 16) * ratio + parseInt(color2.substring(5, 7), 16) * (1 - ratio))
  return `#${hex(r)}${hex(g)}${hex(b)}`
}

// 生成 Element Plus primary 色阶
export function genPrimaryColors(color) {
  setCssVar('--el-color-primary', color)
  setCssVar('--el-color-primary-light-1', mix(color, '#ffffff', 0.06))
  setCssVar('--el-color-primary-light-3', mix(color, '#ffffff', 0.3))
  setCssVar('--el-color-primary-light-5', mix(color, '#ffffff', 0.5))
  setCssVar('--el-color-primary-light-7', mix(color, '#ffffff', 0.7))
  setCssVar('--el-color-primary-light-8', mix(color, '#ffffff', 0.8))
  setCssVar('--el-color-primary-light-9', mix(color, '#ffffff', 0.9))
  setCssVar('--el-color-primary-dark-2', mix(color, '#000000', 0.8))
}

// 注入全局主题色样式标签（直接覆盖所有 Element Plus 组件颜色）
export function injectThemeStyle(color) {
  const id = 'app-theme-style'
  let el = document.getElementById(id)
  if (!el) {
    el = document.createElement('style')
    el.id = id
    document.head.appendChild(el)
  }
  const light1 = mix(color, '#ffffff', 0.06)
  const light3 = mix(color, '#ffffff', 0.7)
  const light5 = mix(color, '#ffffff', 0.5)
  const light7 = mix(color, '#ffffff', 0.3)
  const light8 = mix(color, '#ffffff', 0.2)
  const light9 = mix(color, '#ffffff', 0.1)
  const dark2 = mix(color, '#000000', 0.8)

  el.textContent = `
    /* ===== Element Plus 主题色全局注入 ===== */
    .el-button--primary { background-color: ${color} !important; border-color: ${color} !important; }
    .el-button--primary:hover,
    .el-button--primary:focus { background-color: ${dark2} !important; border-color: ${dark2} !important; }
    .el-button--primary:active { background-color: ${dark2} !important; }
    .el-button--primary.is-plain { background: ${light9} !important; border-color: ${light7} !important; color: ${color} !important; }
    .el-button--primary.is-plain:hover,
    .el-button--primary.is-plain:focus { background: ${color} !important; border-color: ${color} !important; color: #fff !important; }
    .el-button--primary.is-disabled,
    .el-button--primary.is-disabled:hover,
    .el-button--primary.is-disabled:focus,
    .el-button--primary.is-disabled:active { background-color: ${light5} !important; border-color: ${light5} !important; }
    .el-button--primary.is-text { background: transparent !important; border: none !important; color: ${color} !important; }

    /* 覆盖旧主题系统的 bg_xxx 类（border 用简写对抗 main.css 的 border 简写） */
    .el-button.bg_4f7afd,
    .el-button.bg_f05b72,
    .el-button.bg_426ab3,
    .el-button.bg_faa755,
    .el-button.bg_65c294,
    .el-button.bg_c88400,
    .el-button.bg_9b95c9,
    .el-button.bg_002FA7,
    .el-button.bg_FF6347,
    .el-button.bg_B05923,
    .el-button.bg_2e3a1f { background-color: ${color} !important; border: 1px solid ${color} !important; }
    .el-button.bg_4f7afd:hover,
    .el-button.bg_f05b72:hover,
    .el-button.bg_426ab3:hover,
    .el-button.bg_faa755:hover,
    .el-button.bg_65c294:hover,
    .el-button.bg_c88400:hover,
    .el-button.bg_9b95c9:hover,
    .el-button.bg_002FA7:hover,
    .el-button.bg_FF6347:hover,
    .el-button.bg_B05923:hover,
    .el-button.bg_2e3a1f:hover { background-color: ${dark2} !important; border: 1px solid ${dark2} !important; }

    /* 分页 */
    .el-pager li.is-active { background-color: ${color} !important; color: #fff !important; }
    .el-pager li:hover { color: ${color} !important; }
    .el-pagination button:hover { color: ${color} !important; }

    /* 开关 */
    .el-switch.is-checked .el-switch__core { background-color: ${color} !important; border-color: ${color} !important; }

    /* 复选框 */
    .el-checkbox__input.is-checked .el-checkbox__inner { background-color: ${color} !important; border-color: ${color} !important; }
    .el-checkbox__input.is-checked+.el-checkbox__label { color: ${color} !important; }
    .el-checkbox__input.is-indeterminate .el-checkbox__inner { background-color: ${color} !important; border-color: ${color} !important; }

    /* 单选框 */
    .el-radio__input.is-checked .el-radio__inner { background-color: ${color} !important; border-color: ${color} !important; }
    .el-radio__input.is-checked+.el-radio__label { color: ${color} !important; }

    /* 输入框焦点 */
    .el-input__inner:focus { border-color: ${color} !important; }
    .el-textarea__inner:focus { border-color: ${color} !important; }
    .el-select .el-input.is-focus .el-input__inner { border-color: ${color} !important; }

    /* 下拉菜单 */
    .el-select-dropdown__item.selected { color: ${color} !important; }
    .el-dropdown-menu__item:not(.is-disabled):hover { color: ${color} !important; }

    /* 标签页 */
    .el-tabs__item.is-active { color: ${color} !important; }
    .el-tabs__item:hover { color: ${color} !important; }
    .el-tabs__active-bar { background-color: ${color} !important; }

    /* 滑条 */
    .el-slider__bar { background-color: ${color} !important; }
    .el-slider__button { border-color: ${color} !important; }

    /* 评分 */
    .el-rate__icon.is-active { color: ${color} !important; }

    /* 加载 */
    .el-loading-spinner .circular circle { stroke: ${color}; }

    /* 菜单 — 白色模式一级菜单激活态：背景为主题色，文字为黑色 */
    .el-menu-item.is-active { color: #000 !important; background-color: ${color} !important; }
    .sidebar .el-menu-item.is-active span,
    .sidebar .el-menu-item.is-active i,
    .sidebar .el-menu-item.is-active .icon,
    .sidebar .el-menu-item.is-active * { color: #000 !important; }
    /* 有二级菜单的一级菜单（el-sub-menu__title）：只改文字颜色，不加背景 */
    .sidebar .el-sub-menu.is-active > .el-sub-menu__title { color: #000 !important; background: transparent !important; }
    .sidebar .el-sub-menu.is-active > .el-sub-menu__title span,
    .sidebar .el-sub-menu.is-active > .el-sub-menu__title i,
    .sidebar .el-sub-menu.is-active > .el-sub-menu__title .icon { color: #000 !important; background: transparent !important; }
    /* bg_xxx3 通用：只改文字颜色，不加背景 */
    .sidebar .bg_4f7afd3,
    .sidebar .bg_f05b723,
    .sidebar .bg_426ab33,
    .sidebar .bg_2e3a1f3,
    .sidebar .bg_faa7553,
    .sidebar .bg_65c2943,
    .sidebar .bg_c884003,
    .sidebar .bg_9b95c93,
    .sidebar .bg_002FA73,
    .sidebar .bg_FF63473,
    .sidebar .bg_B059233 { color: #000 !important; background: transparent !important; }
    /* 独立一级菜单项（无二级菜单）：加背景色 */
    .sidebar .el-menu-item.bg_4f7afd3,
    .sidebar .el-menu-item.bg_f05b723,
    .sidebar .el-menu-item.bg_426ab33,
    .sidebar .el-menu-item.bg_2e3a1f3,
    .sidebar .el-menu-item.bg_faa7553,
    .sidebar .el-menu-item.bg_65c2943,
    .sidebar .el-menu-item.bg_c884003,
    .sidebar .el-menu-item.bg_9b95c93,
    .sidebar .el-menu-item.bg_002FA73,
    .sidebar .el-menu-item.bg_FF63473,
    .sidebar .el-menu-item.bg_B059233 { background-color: ${color} !important; }

    /* 暗色模式下一级菜单激活态：背景为主题色，文字为白色 */
    .dark .el-menu-item.is-active { color: #fff !important; background-color: ${color} !important; }
    .dark .sidebar .el-menu-item.is-active span,
    .dark .sidebar .el-menu-item.is-active i,
    .dark .sidebar .el-menu-item.is-active .icon,
    .dark .sidebar .el-menu-item.is-active * { color: #fff !important; }
    /* 有二级菜单的一级菜单（el-sub-menu__title）：只改文字颜色，不加背景 */
    .dark .sidebar .el-sub-menu.is-active > .el-sub-menu__title { background: transparent !important; }
    .dark .sidebar .el-sub-menu.is-active > .el-sub-menu__title span,
    .dark .sidebar .el-sub-menu.is-active > .el-sub-menu__title i,
    .dark .sidebar .el-sub-menu.is-active > .el-sub-menu__title .icon { color: #fff !important; background: transparent !important; }
    /* bg_xxx3 通用：只改文字颜色，不加背景 */
    .dark .sidebar .bg_4f7afd3,
    .dark .sidebar .bg_f05b723,
    .dark .sidebar .bg_426ab33,
    .dark .sidebar .bg_2e3a1f3,
    .dark .sidebar .bg_faa7553,
    .dark .sidebar .bg_65c2943,
    .dark .sidebar .bg_c884003,
    .dark .sidebar .bg_9b95c93,
    .dark .sidebar .bg_002FA73,
    .dark .sidebar .bg_FF63473,
    .dark .sidebar .bg_B059233 { color: #fff !important; background: transparent !important; }
    /* 独立一级菜单项（无二级菜单）：加背景色 */
    .dark .sidebar .el-menu-item.bg_4f7afd3,
    .dark .sidebar .el-menu-item.bg_f05b723,
    .dark .sidebar .el-menu-item.bg_426ab33,
    .dark .sidebar .el-menu-item.bg_2e3a1f3,
    .dark .sidebar .el-menu-item.bg_faa7553,
    .dark .sidebar .el-menu-item.bg_65c2943,
    .dark .sidebar .el-menu-item.bg_c884003,
    .dark .sidebar .el-menu-item.bg_9b95c93,
    .dark .sidebar .el-menu-item.bg_002FA73,
    .dark .sidebar .el-menu-item.bg_FF63473,
    .dark .sidebar .el-menu-item.bg_B059233 { background-color: ${color} !important; }

    /* 二级菜单激活态（对应 theme6）：背景为主题色，文字为黑色 */
    .sidebar .el-menu .el-menu-item.bg_4f7afd6,
    .sidebar .el-menu .el-menu-item.bg_f05b726,
    .sidebar .el-menu .el-menu-item.bg_426ab36,
    .sidebar .el-menu .el-menu-item.bg_2e3a1f6,
    .sidebar .el-menu .el-menu-item.bg_faa7556,
    .sidebar .el-menu .el-menu-item.bg_65c2946,
    .sidebar .el-menu .el-menu-item.bg_c884006,
    .sidebar .el-menu .el-menu-item.bg_9b95c96,
    .sidebar .el-menu .el-menu-item.bg_002FA76,
    .sidebar .el-menu .el-menu-item.bg_FF63476,
    .sidebar .el-menu .el-menu-item.bg_B059236,
    .sidebar .el-sub-menu .el-menu-item.bg_4f7afd6,
    .sidebar .el-sub-menu .el-menu-item.bg_f05b726,
    .sidebar .el-sub-menu .el-menu-item.bg_426ab36,
    .sidebar .el-sub-menu .el-menu-item.bg_2e3a1f6,
    .sidebar .el-sub-menu .el-menu-item.bg_faa7556,
    .sidebar .el-sub-menu .el-menu-item.bg_65c2946,
    .sidebar .el-sub-menu .el-menu-item.bg_c884006,
    .sidebar .el-sub-menu .el-menu-item.bg_9b95c96,
    .sidebar .el-sub-menu .el-menu-item.bg_002FA76,
    .sidebar .el-sub-menu .el-menu-item.bg_FF63476,
    .sidebar .el-sub-menu .el-menu-item.bg_B059236 { background-color: ${color} !important; color: #000 !important; }
    .sidebar .el-menu .el-menu-item.bg_4f7afd6 span,
    .sidebar .el-menu .el-menu-item.bg_f05b726 span,
    .sidebar .el-menu .el-menu-item.bg_426ab36 span,
    .sidebar .el-menu .el-menu-item.bg_2e3a1f6 span,
    .sidebar .el-menu .el-menu-item.bg_faa7556 span,
    .sidebar .el-menu .el-menu-item.bg_65c2946 span,
    .sidebar .el-menu .el-menu-item.bg_c884006 span,
    .sidebar .el-menu .el-menu-item.bg_9b95c96 span,
    .sidebar .el-menu .el-menu-item.bg_002FA76 span,
    .sidebar .el-menu .el-menu-item.bg_FF63476 span,
    .sidebar .el-menu .el-menu-item.bg_B059236 span,
    .sidebar .el-sub-menu .el-menu-item.bg_4f7afd6 span,
    .sidebar .el-sub-menu .el-menu-item.bg_f05b726 span,
    .sidebar .el-sub-menu .el-menu-item.bg_426ab36 span,
    .sidebar .el-sub-menu .el-menu-item.bg_2e3a1f6 span,
    .sidebar .el-sub-menu .el-menu-item.bg_faa7556 span,
    .sidebar .el-sub-menu .el-menu-item.bg_65c2946 span,
    .sidebar .el-sub-menu .el-menu-item.bg_c884006 span,
    .sidebar .el-sub-menu .el-menu-item.bg_9b95c96 span,
    .sidebar .el-sub-menu .el-menu-item.bg_002FA76 span,
    .sidebar .el-sub-menu .el-menu-item.bg_FF63476 span,
    .sidebar .el-sub-menu .el-menu-item.bg_B059236 span { color: #000 !important; }
    .sidebar .el-menu .el-menu-item.bg_4f7afd6 i,
    .sidebar .el-menu .el-menu-item.bg_f05b726 i,
    .sidebar .el-menu .el-menu-item.bg_426ab36 i,
    .sidebar .el-menu .el-menu-item.bg_2e3a1f6 i,
    .sidebar .el-menu .el-menu-item.bg_faa7556 i,
    .sidebar .el-menu .el-menu-item.bg_65c2946 i,
    .sidebar .el-menu .el-menu-item.bg_c884006 i,
    .sidebar .el-menu .el-menu-item.bg_9b95c96 i,
    .sidebar .el-menu .el-menu-item.bg_002FA76 i,
    .sidebar .el-menu .el-menu-item.bg_FF63476 i,
    .sidebar .el-menu .el-menu-item.bg_B059236 i,
    .sidebar .el-sub-menu .el-menu-item.bg_4f7afd6 i,
    .sidebar .el-sub-menu .el-menu-item.bg_f05b726 i,
    .sidebar .el-sub-menu .el-menu-item.bg_426ab36 i,
    .sidebar .el-sub-menu .el-menu-item.bg_2e3a1f6 i,
    .sidebar .el-sub-menu .el-menu-item.bg_faa7556 i,
    .sidebar .el-sub-menu .el-menu-item.bg_65c2946 i,
    .sidebar .el-sub-menu .el-menu-item.bg_c884006 i,
    .sidebar .el-sub-menu .el-menu-item.bg_9b95c96 i,
    .sidebar .el-sub-menu .el-menu-item.bg_002FA76 i,
    .sidebar .el-sub-menu .el-menu-item.bg_FF63476 i,
    .sidebar .el-sub-menu .el-menu-item.bg_B059236 i { color: #000 !important; }
    .sidebar .el-menu .el-menu-item.bg_4f7afd6 .icon,
    .sidebar .el-menu .el-menu-item.bg_f05b726 .icon,
    .sidebar .el-menu .el-menu-item.bg_426ab36 .icon,
    .sidebar .el-menu .el-menu-item.bg_2e3a1f6 .icon,
    .sidebar .el-menu .el-menu-item.bg_faa7556 .icon,
    .sidebar .el-menu .el-menu-item.bg_65c2946 .icon,
    .sidebar .el-menu .el-menu-item.bg_c884006 .icon,
    .sidebar .el-menu .el-menu-item.bg_9b95c96 .icon,
    .sidebar .el-menu .el-menu-item.bg_002FA76 .icon,
    .sidebar .el-menu .el-menu-item.bg_FF63476 .icon,
    .sidebar .el-menu .el-menu-item.bg_B059236 .icon,
    .sidebar .el-sub-menu .el-menu-item.bg_4f7afd6 .icon,
    .sidebar .el-sub-menu .el-menu-item.bg_f05b726 .icon,
    .sidebar .el-sub-menu .el-menu-item.bg_426ab36 .icon,
    .sidebar .el-sub-menu .el-menu-item.bg_2e3a1f6 .icon,
    .sidebar .el-sub-menu .el-menu-item.bg_faa7556 .icon,
    .sidebar .el-sub-menu .el-menu-item.bg_65c2946 .icon,
    .sidebar .el-sub-menu .el-menu-item.bg_c884006 .icon,
    .sidebar .el-sub-menu .el-menu-item.bg_9b95c96 .icon,
    .sidebar .el-sub-menu .el-menu-item.bg_002FA76 .icon,
    .sidebar .el-sub-menu .el-menu-item.bg_FF63476 .icon,
    .sidebar .el-sub-menu .el-menu-item.bg_B059236 .icon { color: #000 !important; }

    /* 暗色模式下的二级菜单激活态：背景为主题色，文字为白色 */
    .dark .sidebar .el-menu .bg_4f7afd6,
    .dark .sidebar .el-menu .bg_f05b726,
    .dark .sidebar .el-menu .bg_426ab36,
    .dark .sidebar .el-menu .bg_2e3a1f6,
    .dark .sidebar .el-menu .bg_faa7556,
    .dark .sidebar .el-menu .bg_65c2946,
    .dark .sidebar .el-menu .bg_c884006,
    .dark .sidebar .el-menu .bg_9b95c96,
    .dark .sidebar .el-menu .bg_002FA76,
    .dark .sidebar .el-menu .bg_FF63476,
    .dark .sidebar .el-menu .bg_B059236,
    .dark .sidebar .el-sub-menu .bg_4f7afd6,
    .dark .sidebar .el-sub-menu .bg_f05b726,
    .dark .sidebar .el-sub-menu .bg_426ab36,
    .dark .sidebar .el-sub-menu .bg_2e3a1f6,
    .dark .sidebar .el-sub-menu .bg_faa7556,
    .dark .sidebar .el-sub-menu .bg_65c2946,
    .dark .sidebar .el-sub-menu .bg_c884006,
    .dark .sidebar .el-sub-menu .bg_9b95c96,
    .dark .sidebar .el-sub-menu .bg_002FA76,
    .dark .sidebar .el-sub-menu .bg_FF63476,
    .dark .sidebar .el-sub-menu .bg_B059236 { background-color: ${color} !important; color: #fff !important; }
    .dark .sidebar .el-menu .bg_4f7afd6 span,
    .dark .sidebar .el-menu .bg_f05b726 span,
    .dark .sidebar .el-menu .bg_426ab36 span,
    .dark .sidebar .el-menu .bg_2e3a1f6 span,
    .dark .sidebar .el-menu .bg_faa7556 span,
    .dark .sidebar .el-menu .bg_65c2946 span,
    .dark .sidebar .el-menu .bg_c884006 span,
    .dark .sidebar .el-menu .bg_9b95c96 span,
    .dark .sidebar .el-menu .bg_002FA76 span,
    .dark .sidebar .el-menu .bg_FF63476 span,
    .dark .sidebar .el-menu .bg_B059236 span,
    .dark .sidebar .el-sub-menu .bg_4f7afd6 span,
    .dark .sidebar .el-sub-menu .bg_f05b726 span,
    .dark .sidebar .el-sub-menu .bg_426ab36 span,
    .dark .sidebar .el-sub-menu .bg_2e3a1f6 span,
    .dark .sidebar .el-sub-menu .bg_faa7556 span,
    .dark .sidebar .el-sub-menu .bg_65c2946 span,
    .dark .sidebar .el-sub-menu .bg_c884006 span,
    .dark .sidebar .el-sub-menu .bg_9b95c96 span,
    .dark .sidebar .el-sub-menu .bg_002FA76 span,
    .dark .sidebar .el-sub-menu .bg_FF63476 span,
    .dark .sidebar .el-sub-menu .bg_B059236 span { color: #fff !important; }
    .dark .sidebar .el-menu .bg_4f7afd6 i,
    .dark .sidebar .el-menu .bg_f05b726 i,
    .dark .sidebar .el-menu .bg_426ab36 i,
    .dark .sidebar .el-menu .bg_2e3a1f6 i,
    .dark .sidebar .el-menu .bg_faa7556 i,
    .dark .sidebar .el-menu .bg_65c2946 i,
    .dark .sidebar .el-menu .bg_c884006 i,
    .dark .sidebar .el-menu .bg_9b95c96 i,
    .dark .sidebar .el-menu .bg_002FA76 i,
    .dark .sidebar .el-menu .bg_FF63476 i,
    .dark .sidebar .el-menu .bg_B059236 i,
    .dark .sidebar .el-sub-menu .bg_4f7afd6 i,
    .dark .sidebar .el-sub-menu .bg_f05b726 i,
    .dark .sidebar .el-sub-menu .bg_426ab36 i,
    .dark .sidebar .el-sub-menu .bg_2e3a1f6 i,
    .dark .sidebar .el-sub-menu .bg_faa7556 i,
    .dark .sidebar .el-sub-menu .bg_65c2946 i,
    .dark .sidebar .el-sub-menu .bg_c884006 i,
    .dark .sidebar .el-sub-menu .bg_9b95c96 i,
    .dark .sidebar .el-sub-menu .bg_002FA76 i,
    .dark .sidebar .el-sub-menu .bg_FF63476 i,
    .dark .sidebar .el-sub-menu .bg_B059236 i { color: #fff !important; }
    .dark .sidebar .el-menu .bg_4f7afd6 .icon,
    .dark .sidebar .el-menu .bg_f05b726 .icon,
    .dark .sidebar .el-menu .bg_426ab36 .icon,
    .dark .sidebar .el-menu .bg_2e3a1f6 .icon,
    .dark .sidebar .el-menu .bg_faa7556 .icon,
    .dark .sidebar .el-menu .bg_65c2946 .icon,
    .dark .sidebar .el-menu .bg_c884006 .icon,
    .dark .sidebar .el-menu .bg_9b95c96 .icon,
    .dark .sidebar .el-menu .bg_002FA76 .icon,
    .dark .sidebar .el-menu .bg_FF63476 .icon,
    .dark .sidebar .el-menu .bg_B059236 .icon,
    .dark .sidebar .el-sub-menu .bg_4f7afd6 .icon,
    .dark .sidebar .el-sub-menu .bg_f05b726 .icon,
    .dark .sidebar .el-sub-menu .bg_426ab36 .icon,
    .dark .sidebar .el-sub-menu .bg_2e3a1f6 .icon,
    .dark .sidebar .el-sub-menu .bg_faa7556 .icon,
    .dark .sidebar .el-sub-menu .bg_65c2946 .icon,
    .dark .sidebar .el-sub-menu .bg_c884006 .icon,
    .dark .sidebar .el-sub-menu .bg_9b95c96 .icon,
    .dark .sidebar .el-sub-menu .bg_002FA76 .icon,
    .dark .sidebar .el-sub-menu .bg_FF63476 .icon,
    .dark .sidebar .el-sub-menu .bg_B059236 .icon { color: #fff !important; }

    /* 进度条 */
    .el-progress-bar__inner { background-color: ${color} !important; }

    /* 走马灯 */
    .el-carousel__indicator.is-active button { background-color: ${color} !important; }

    /* Tag */
    .el-tag--primary { background-color: ${light9} !important; border-color: ${light7} !important; color: ${color} !important; }
    .el-tag--primary .el-tag__close:hover { background-color: ${color} !important; color: #fff !important; }

    /* 步骤条 */
    .el-step__head.is-process { color: ${color} !important; border-color: ${color} !important; }
    .el-step__title.is-process { color: ${color} !important; }

    /* 级联选择器 */
    .el-cascader-node.in-active-path,
    .el-cascader-node.is-active,
    .el-cascader-node.is-selectable.in-checked-path { color: ${color} !important; }

    /* 时间/日期选择器 */
    .el-date-table td.today span { color: ${color} !important; }
    .el-date-table td.available:hover { color: ${color} !important; }
    .el-date-table td.current:not(.disabled) span { background-color: ${color} !important; }
    .el-picker-panel__icon-btn:hover { color: ${color} !important; }
    .el-date-picker__header-label:hover { color: ${color} !important; }
    .el-time-spinner__item.active:not(.disabled) { color: ${color} !important; }

    /* 面包屑 */
    .el-breadcrumb__inner:hover { color: ${color} !important; }

    /* 链接 */
    a { color: ${color}; }
    a:hover { color: ${dark2}; }

    /* 全局 hover 色 */
    .el-tree-node__content:hover { background-color: ${light9} !important; }
    .el-tree--highlight-current .el-tree-node.is-current>.el-tree-node__content { background-color: ${light9} !important; }

    /* collapsed 菜单 active */
    .el-menu--collapse .el-menu-item.is-active { color: ${color} !important; }

    /* ===== 暗色模式下的 Element Plus 组件主题色覆盖 ===== */
    .dark .el-button--primary { background-color: ${color} !important; border-color: ${color} !important; }
    .dark .el-button--primary:hover,
    .dark .el-button--primary:focus { background-color: ${dark2} !important; border-color: ${dark2} !important; }
    .dark .el-button--primary.is-plain { background: ${light9} !important; border-color: ${light7} !important; color: ${color} !important; }
    .dark .el-pager li.is-active { background: ${color} !important; }
    .dark .el-pager li:hover,
    .dark .el-pagination button:hover { color: ${color} !important; }
    .dark .el-tabs__item.is-active,
    .dark .el-tabs__item:hover { color: ${color} !important; }
    .dark .el-tabs__active-bar { background: ${color} !important; }
    .dark .el-switch.is-checked .el-switch__core { background-color: ${color} !important; border-color: ${color} !important; }
    .dark .el-checkbox__input.is-checked .el-checkbox__inner { background-color: ${color} !important; border-color: ${color} !important; }
    .dark .el-checkbox__input.is-checked+.el-checkbox__label { color: ${color} !important; }
    .dark .el-radio__input.is-checked .el-radio__inner { background-color: ${color} !important; border-color: ${color} !important; }
    .dark .el-select-dropdown__item.selected { color: ${color} !important; }
    .dark .el-dropdown-menu__item:hover { color: ${color} !important; }
    .dark .el-cascader-node.is-active,
    .dark .el-cascader-node.in-active-path { color: ${color} !important; }
    .dark .el-date-table td.today span,
    .dark .el-date-table td.available:hover { color: ${color} !important; }
    .dark .el-date-table td.current:not(.disabled) span { background: ${color} !important; }
    .dark .el-date-picker__header-label:hover,
    .dark .el-picker-panel__icon-btn:hover { color: ${color} !important; }
    .dark .el-time-spinner__item.active:not(.disabled) { color: ${color} !important; }
    .dark .el-loading-spinner .el-loading-text { color: ${color} !important; }
    .dark .el-breadcrumb__inner:hover { color: ${color} !important; }
    .dark .el-tag--primary { background: ${light9} !important; border-color: ${light7} !important; color: ${color} !important; }
    .dark .el-slider__bar { background-color: ${color} !important; }
    .dark .el-progress-bar__inner { background-color: ${color} !important; }
    .dark .el-input__inner:focus,
    .dark .el-textarea__inner:focus { border-color: ${color} !important; }
    .dark .el-select .el-input.is-focus .el-input__inner { border-color: ${color} !important; }
    .dark .el-button--default:hover { border-color: ${color} !important; color: ${color} !important; }
    .dark .el-button--text:hover { color: ${color} !important; }
    .dark .handle-box .el-button--primary { background: ${color} !important; border-color: ${color} !important; }
    .dark .tags-li.active { background: ${color} !important; border-color: ${color} !important; }
    .dark .statistics .ui-box .label-num { color: ${color} !important; }
    .dark .statistics .tit-btn .active,
    .dark .box-card .fl_r .active,
    .dark .dashboard_box .fl_r .active { background: ${color} !important; }
    .dark .text:hover { color: ${color} !important; }
    .dark .chart-title i { color: ${color} !important; }
    .dark .nav-tab .active { color: ${color} !important; }
    .dark .grid-num { color: ${color} !important; }
    .dark .el-step__head.is-process { color: ${color} !important; border-color: ${color} !important; }
    .dark .el-step__title.is-process { color: ${color} !important; }
    .dark .el-switch__label.is-active { color: ${color} !important; }
    .dark .el-backtop { color: ${color} !important; }
    .dark .el-step__head.is-process { color: ${color} !important; border-color: ${color} !important; }
    .dark .el-step__title.is-process { color: ${color} !important; }
  `
}

// 获取系统暗色偏好
export function getSystemDarkMode() {
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
}

// localStorage 读写
export function lsGet(key, def) {
  try {
    const v = localStorage.getItem(key)
    if (v === null) return def
    if (v === 'true') return true
    if (v === 'false') return false
    return v
  } catch { return def }
}

export function lsSet(key, val) {
  try { localStorage.setItem(key, String(val)) } catch { /* ignore */ }
}
